import { ActivityStats, Period, useUnits } from '@geovelo-frontends/commons';
import { Chart, TooltipItem } from 'chart.js';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

interface IProps {
  activities?: Array<ActivityStats | null>;
  disablePrevPeriod?: boolean;
  period: Period;
}

function CommunityChart({ activities, disablePrevPeriod, period }: IProps): React.JSX.Element {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();
  const { toDistance } = useUnits();

  useEffect(() => {
    let chart: Chart | undefined;
    if (chartRef.current) {
      // @ts-expect-error typings are uncorrect
      chart = new Chart(chartRef.current, {
        type: 'bar',
        data: {
          datasets: [
            {
              type: 'line',
              hidden: true,
              label: t('commons.periods.prev') || '',
              data: (activities?.[1]?.data && !disablePrevPeriod
                ? activities[1].data.map(({ distance, date }) => {
                    const actualDate = moment(date);
                    if (period.type === 'year') actualDate.add(1, 'years');
                    else if (period.type === 'month') actualDate.add(1, 'months');
                    else actualDate.add(7, 'days');

                    return {
                      initialDate: date,
                      x: actualDate.format('YYYY-MM-DD HH:mm'),
                      y: distance,
                    };
                  })
                : []
              ).sort((a, b) => (a.initialDate.isBefore(b.initialDate) ? -1 : 1)),
              backgroundColor: '#6c6c6c',
            },
            {
              label: t('commons.periods.current_one') || '',
              barPercentage: 0.8,
              borderRadius: 4,
              data: activities?.[0]?.data
                ? activities[0].data.map(({ distance, date }) => ({
                    x: date.format('YYYY-MM-DD HH:mm'),
                    y: distance,
                  }))
                : [],
              backgroundColor: '#2ac682',
            },
          ],
        },
        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: period.unit,
                parser: 'YYYY-MM-DD HH:mm',
              },
              min: period.from.format('YYYY-MM-DD HH:mm'),
              max: period.to.format('YYYY-MM-DD HH:mm'),
              grid: { display: false },
              ticks: {
                callback: (_, index, ticks) =>
                  moment(ticks[index].value).format(
                    period.unit === 'month' ? 'MMM' : period.type === 'week' ? 'dddd' : 'Do',
                  ),
              },
            },
            y: {
              border: { dash: [4, 8] },
              beginAtZero: true,
              suggestedMax: 10000,
              ticks: {
                stepSize: 10000,
                callback: (value) => toDistance(value),
              },
            },
          },
          plugins: {
            legend: disablePrevPeriod
              ? { display: false }
              : {
                  align: 'end',
                  position: 'top',
                  onClick: (_, { datasetIndex, hidden }) => {
                    if (datasetIndex === 0) {
                      if (hidden) chart?.show(datasetIndex);
                      else chart?.hide(datasetIndex);
                    }
                  },
                },
            tooltip: {
              mode: 'point',
              callbacks: {
                title: titleCallback(period),
                label: ({ parsed }) => ('y' in parsed ? toDistance(parsed.y) : ''),
              },
            },
          },
          maintainAspectRatio: false,
          responsive: true,
        },
      });
    }

    return () => {
      if (chart) chart.destroy();
    };
  }, [activities, period]);

  function titleCallback<T extends 'bar' | 'line'>(period: Period) {
    return (items: TooltipItem<T>[]): string => {
      const { raw } = items[0];
      const { initialDate, x } = raw as { initialDate?: Moment; x: string };

      const date = initialDate || moment(x);

      return period.type === 'year'
        ? date.format('MMMM YYYY')
        : period.type === 'month'
          ? date.format('Do MMM')
          : date.format('dddd Do MMM');
    };
  }

  return (
    <Wrapper>
      <StyledChart id={`${period.type}-chart`} ref={chartRef} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex-grow: 1;
  height: 300px;
  overflow-x: hidden;
`;

const StyledChart = styled.canvas`
  height: 100%;
  width: 100%;
`;

export default CommunityChart;
